import { Dispatch, SetStateAction } from "react";
import { MRT_Row, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table";
import {
  BookingServiceEditedData,
  OrderableCateringEditedData,
  ServiceOrderDto
} from "../Domain/BookingServicePrompt.types";
import { CateringTableRow } from "./CateringTableRow.types";
import * as _ from "lodash";
import { BookingInputs } from "../../../../../Domain/Types/BookingInputs.type";
import { isServiceItemOrderable } from "../functions/filterByServiceTime.function";
import { getLowestLeadTimeEntry } from "../functions/getLowestLeadTimeEntry.function";

export const updateQuantityOfCatering = (
  row: MRT_Row<CateringTableRow>,
  quantity: number,
  editedCateringData: OrderableCateringEditedData[],
  setEditedService: Dispatch<SetStateAction<BookingServiceEditedData>>,
  orderedService: ServiceOrderDto,
  setOrderedService: Dispatch<SetStateAction<ServiceOrderDto>>
) => {
  if (!editedCateringData?.length) return;

  const editedIndex = editedCateringData.findIndex(d => d.id === row.original.id);
  const orderIndex = orderedService.caterings.findIndex(d => d.id === row.original.id);

  if (editedIndex >= 0) {
    editedCateringData[editedIndex].qty = quantity;
    setEditedService(prev => ({ ...prev, cateringData: editedCateringData }));
  }

  if (orderIndex >= 0) {
    orderedService.caterings[orderIndex].qty = quantity;
    setOrderedService(orderedService);
  } else {
    const selectedItem = {
      id: Number(row.id),
      qty: quantity,
      name: row.original.name,
      setUpTime: row.original.setUpTime,
      bookingLeadTime: row.original.bookingLeadTime,
      pricePerUnit: row.original.pricePerUnit
    };
    const newCaterings = [...orderedService.caterings, selectedItem];

    setOrderedService(prev => ({ ...prev, caterings: newCaterings }));
  }
};

export function intiateCateringData({
  bookingInputs,
  maxSetupTime,
  assignedCateringItems,
  conferenceService,
  isServiceAdminBypass = false,
  setEditedService,
  setOrderedService
}: {
  bookingInputs: BookingInputs;
  maxSetupTime: number;
  assignedCateringItems: any[];
  conferenceService: ServiceOrderDto | undefined;
  isServiceAdminBypass?: boolean;
  setEditedService: (value: SetStateAction<BookingServiceEditedData>) => void;
  setOrderedService: (value: SetStateAction<ServiceOrderDto>) => void;
}) {
  setEditedService(prev => ({
    ...prev,
    cateringData: mapCateringEditedData(
      bookingInputs,
      assignedCateringItems,
      conferenceService,
      maxSetupTime,
      isServiceAdminBypass,
      true
    )
  }));
  if (conferenceService?.caterings && conferenceService?.caterings.length > 0) {
    setOrderedService(prev => ({
      ...prev,
      caterings: mapCateringEditedData(
        bookingInputs,
        assignedCateringItems,
        conferenceService,
        maxSetupTime,
        isServiceAdminBypass,
        false
      )
    }));
  }
}

export const handleInitialCatering = (
  serviceOrder: ServiceOrderDto,
  editedService: BookingServiceEditedData,
  setServiceEditedData: Dispatch<SetStateAction<BookingServiceEditedData>>,
  handleInitialRowSelection: (newSelection: string[]) => void,
  handleRowSelectionChange: (newSelection: MRT_RowSelectionState) => void,
  isMandatory: boolean
) => {
  const orderedCatering = _.uniqBy(
    serviceOrder.caterings.flatMap(ent => ent ?? []),
    prop => prop.id
  ).filter(p => p.qty > 0);

  if (!orderedCatering.length) {
    if (isMandatory) {
      const lowest = getLowestLeadTimeEntry(editedService.cateringData);
      return handleRowSelectionChange({ [lowest.id]: true });
    } else return;
  }

  const editedCatering = editedService.cateringData;

  // initial row select
  handleInitialRowSelection(orderedCatering.map(cat => cat.id.toString()));

  // update quantity if they already have value
  const initialEdited = editedCatering.map(item => ({
    ...item,
    qty: orderedCatering.find(cat => cat.id === item.id)?.qty ?? 0
  }));

  setServiceEditedData(prev => ({
    ...prev,
    cateringData: initialEdited
  }));
};

/**
 * mapping bookable equipment table data filter by service time and include/exclude based on the orderable boolean
 */
export function mapCateringEditedData(
  bookingInputs: BookingInputs,
  assignedCateringItems: any[],
  conferenceService: ServiceOrderDto | undefined,
  maxSetupTime: number,
  isServiceAdminBypass = false,
  includeOrderable = true
) {
  const existingQtyMap = new Map(conferenceService?.caterings?.map(c => [c.id, c.qty]) ?? []);

  return assignedCateringItems
    .map(item => {
      const setUpTime = item.setUpTime ?? 15;
      const bookingLeadTime = item.bookingLeadTime ?? 1;

      const orderable = isServiceItemOrderable({
        bookingFrom: bookingInputs.bookingFrom,
        bookingStart: bookingInputs.bookingStart,
        setUpTime,
        bookingLeadTime,
        maxSetupTime,
        isServiceAdminBypass
      });

      if (!orderable) return null; // always filter non-orderable items

      const base = {
        ...item,
        id: item.id,
        setUpTime,
        bookingLeadTime,
        qty: existingQtyMap.get(item.id) ?? 0,
        selectingIsMandatory: item.selectingIsMandatory ?? false
      };

      return includeOrderable ? { ...base, orderable } : base;
    })
    .filter(Boolean);
}

/** checking deselected row and reset deselected row quantity */
export const getUniqueKeys = (
  first: Record<number, boolean>,
  second: Record<number, boolean>
): number[] | null => {
  const result = Object.keys(second)
    .map(Number) // Convert keys to numbers
    .filter(key => !(key in first));

  return result.length > 0 ? result : null;
};

export const handleDeselectedRowQuantity = (
  ids: number[] | null,
  table: MRT_TableInstance<CateringTableRow>,
  updateQuantity: (upd: MRT_Row<CateringTableRow>) => void
) => {
  if (!ids?.length) return;

  const deselectedRow = table.getRowModel().rows.find(row => row.original.id === ids[0]);

  if (!deselectedRow) return;

  updateQuantity(deselectedRow);
};
